import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import DepositForm from "./DepositForm";
import PasswordInput from "./PasswordInput";
import {
  FiHome,
  FiGrid,
  FiPackage,
  FiDollarSign,
  FiLayers,
  FiHelpCircle,
  FiCode,
  FiLogOut,
  FiMenu,
  FiX,
  FiUsers,
  FiServer,
  FiActivity,
} from 'react-icons/fi';

import priceBG from "../../src/assets/images/price-bg.png"
import logo from "../../src/assets/images/logo.png"
import dashboardBG from "../../src/assets/images/background.png"


const Layout = ({
  children,
  backgroundImage = dashboardBG,
  backgroundPosition = "bottom center",
  backgroundSize = "cover",
  backgroundRepeat = "no-repeat",
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ newEmail: '' });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    const onOpenDeposit = () => {
      setSidebarOpen(false);
      setShowDepositModal(true);
    };

    window.addEventListener('myrdp:open-deposit', onOpenDeposit);
    return () => window.removeEventListener('myrdp:open-deposit', onOpenDeposit);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    if (!emailForm.newEmail) {
      toast.error('Please enter your new email');
      return;
    }
    setSavingEmail(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/users/change-email`,
        {
          newEmail: emailForm.newEmail
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Confirmation link sent to your current email');
      setEmailForm({ newEmail: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Please complete all password fields');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSavingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/users/change-password`,
        {
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const menuItems = [
    { path: '/dashboard', icon: FiHome, label: 'Dashboard' },
    { path: '/services', icon: FiGrid, label: 'My Servers' },
    { path: '/select-plan', icon: FiPackage, label: 'Order Server' },
    { action: 'deposit', icon: FiDollarSign, label: 'Deposit' },
    { path: '/deposits', icon: FiLayers, label: 'Transactions' },
    { path: '/reseller-api', icon: FiCode, label: 'API' },
  ];

  const isActive = (path) => location.pathname === path;
  const isDepositActive = () => showDepositModal || location.pathname === "/topup";

  /** Active = same visual as .myrdp-nav-item:hover (see index.css ::before gradient) */
  const sidebarNavInactiveClass =
    'rounded-lg text-blue-100/75 hover:bg-blue-500/10 hover:text-blue-50';
  const sidebarNavActiveClass =
    'myrdp-nav-item--active rounded-lg text-white outline-none focus:outline-none';

  /** Profile card top line: always show username, never the full email (fallback: local-part only). */
  const sidebarProfileName =
    user?.username?.trim() ||
    (user?.email?.includes('@') ? user.email.split('@')[0] : '') ||
    '';

  const SidebarContent = () => (
    <>
      <div className="p-4">
        <div
          className="flex items-center justify-between rounded-2xl bg-no-repeat bg-right-top p-3 shadow-[0_10px_22px_rgba(15,23,42,0.4)] border border-[#B8B8B847] animate-fadeInSoft"
          style={{ backgroundImage: `url(${priceBG})` }}
        >
          <div className='flex items-center gap-3'>
            <div
              className='group w-[47px] h-[47px] flex items-center justify-center rounded-full bg-gradient-to-b from-[#0B0A24_60%] to-[#1A39AC]
              shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]
              transition-transform duration-300 ease-out hover:scale-105 motion-reduce:transition-none motion-reduce:hover:scale-100
              hover:shadow-[inset_0_0_0_1px_rgba(79,107,255,0.35),0_18px_45px_rgba(60,60,255,0.20)]'
            >
              <svg width="18" height="26" viewBox="0 0 18 26" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.98666 12.3303C8.95098 12.3303 8.92725 12.3303 8.89156 12.3303C8.83215 12.318 8.74884 12.318 8.67748 12.3303C5.2297 12.2231 2.62598 9.51261 2.62598 6.17125C2.62598 2.77017 5.39615 0 8.79647 0C12.1966 0 14.9668 2.77017 14.9668 6.17125C14.955 9.51261 12.3394 12.2231 9.02234 12.3303C9.0104 12.3303 8.99845 12.3303 8.98666 12.3303ZM8.79647 1.78401C6.38294 1.78401 4.40936 3.75788 4.40936 6.17125C4.40936 8.54941 6.26411 10.4636 8.63002 10.5463C8.70122 10.534 8.85588 10.534 9.01039 10.5463C11.3406 10.4391 13.1716 8.52491 13.1834 6.17125C13.1834 3.75788 11.2098 1.78401 8.79647 1.78401Z" fill="white"/>
              <path d="M8.99993 25.3254C6.6697 25.3254 4.32754 24.7297 2.5561 23.5413C0.903485 22.448 0 20.9488 0 19.321C0 17.6917 0.903485 16.1818 2.5561 15.0762C6.12287 12.7103 11.9009 12.7103 15.4439 15.0762C17.0846 16.1696 18 17.6687 18 19.2965C18 20.9258 17.0965 22.4357 15.4439 23.5413C13.6605 24.7297 11.3303 25.3254 8.99993 25.3254ZM3.54288 16.5738C2.40158 17.3349 1.78339 18.3103 1.78339 19.3333C1.78339 20.3424 2.41353 21.3179 3.54288 22.0667C6.50324 24.0528 11.4968 24.0528 14.4571 22.0667C15.5984 21.3056 16.2166 20.3317 16.2166 19.3088C16.2166 18.2981 15.5865 17.3226 14.4571 16.5738C11.4968 14.6015 6.50324 14.6015 3.54288 16.5738Z" fill="white"/>
              </svg>

            </div>
            <div className='flex flex-col items-start gap-1'>
              <p className="text-xs text-[#FFFFFF]">{sidebarProfileName}</p>
              <span className="text-2xl font-semibold text-white">€{user?.balance?.toFixed(0) || '0'}</span>
            </div>            
          </div>
          <div className="mt-1 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setShowSettingsModal(true)}
              className="group relative flex h-8 w-8 items-center justify-center rounded-full animate-fadeInSoft
              transition-transform duration-300 ease-out hover:scale-110 motion-reduce:transition-none motion-reduce:hover:scale-100"
              style={{ animationDelay: '140ms' }}
              title="Open settings"
            >
              <svg
                width="19"
                height="18"
                viewBox="0 0 19 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="block origin-center transition-transform duration-300 ease-out group-hover:scale-110 motion-reduce:transition-none motion-reduce:transform-none"
              >
                <path
                  d="M12.3253 17.9963C12.1399 17.9963 11.9544 17.9701 11.7691 17.9258C11.2216 17.7756 10.7626 17.431 10.4712 16.9453L10.3652 16.769C9.8444 15.8681 9.12922 15.8681 8.60828 16.769L8.51116 16.9362C8.21982 17.431 7.76073 17.7836 7.21329 17.9258C6.65709 18.0759 6.08327 17.9963 5.59758 17.7051L4.07909 16.8304C3.54052 16.521 3.15194 16.0182 2.9842 15.4085C2.82534 14.7988 2.90483 14.1721 3.2138 13.633C3.46978 13.1825 3.54052 12.7764 3.39041 12.5205C3.2403 12.2645 2.86059 12.1144 2.33976 12.1144C1.05065 12.1144 0 11.0634 0 9.77462V8.21969C0 6.93093 1.05065 5.87997 2.33976 5.87997C2.86059 5.87997 3.2403 5.72976 3.39041 5.47383C3.54052 5.21676 3.47865 4.81075 3.2138 4.36031C2.90483 3.82229 2.82534 3.18638 2.9842 2.58579C3.14318 1.97611 3.53165 1.47334 4.07909 1.16395L5.60644 0.289256C6.6041 -0.302229 7.91959 0.0424538 8.52003 1.05822L8.62602 1.23449C9.14685 2.13537 9.86203 2.13537 10.383 1.23449L10.4801 1.06613C11.0804 0.042406 12.3959 -0.302234 13.4024 0.298351L14.921 1.17312C15.4596 1.48137 15.8481 1.98527 16.0158 2.59495C16.1748 3.2035 16.0953 3.83138 15.7863 4.36941C15.5302 4.81985 15.4596 5.22589 15.6097 5.48182C15.7598 5.73888 16.1394 5.88907 16.6604 5.88907C17.9494 5.88907 19 6.93889 19 8.22878V9.7826C19 11.0725 17.9494 12.1235 16.6604 12.1235C16.1394 12.1235 15.7598 12.2736 15.6097 12.5296C15.4596 12.7855 15.5215 13.1916 15.7863 13.642C16.0953 14.1812 16.1835 14.817 16.0158 15.4176C15.8569 16.0262 15.4685 16.5301 14.921 16.8395L13.3937 17.713C13.0581 17.8984 12.6961 17.9963 12.3253 17.9963ZM9.49119 14.7284C10.277 14.7284 11.0098 15.2231 11.5131 16.0978L11.6102 16.265C11.7161 16.4504 11.8927 16.5836 12.1046 16.6359C12.3165 16.6894 12.5284 16.662 12.705 16.5563L14.2323 15.6736C14.4619 15.5405 14.6386 15.3198 14.7092 15.0548C14.7798 14.7909 14.7444 14.5168 14.6121 14.287C14.1087 13.4214 14.047 12.5296 14.4354 11.8494C14.8239 11.1692 15.6273 10.7813 16.6339 10.7813C17.1989 10.7813 17.6493 10.3308 17.6493 9.76552V8.2106C17.6493 7.65437 17.1989 7.19601 16.6339 7.19601C15.6273 7.19601 14.8239 6.80699 14.4354 6.12678C14.047 5.44657 14.1087 4.55479 14.6121 3.68917C14.7444 3.4594 14.7798 3.18643 14.7092 2.92139C14.6386 2.65636 14.4708 2.44479 14.2412 2.3026L12.7137 1.42902C12.3342 1.19925 11.831 1.33117 11.6014 1.72019L11.5042 1.88744C11.0009 2.76215 10.2681 3.25693 9.48243 3.25693C8.69664 3.25693 7.96383 2.76215 7.46052 1.88744L7.3634 1.7111C7.14267 1.34028 6.64822 1.20835 6.26863 1.42902L4.74116 2.3117C4.51168 2.44478 4.33507 2.66546 4.26445 2.93049C4.19383 3.19552 4.22908 3.4685 4.36157 3.69827C4.86477 4.56388 4.92663 5.45567 4.53817 6.13588C4.14971 6.81608 3.34628 7.20399 2.33976 7.20399C1.77469 7.20399 1.32437 7.65437 1.32437 8.21969V9.77462C1.32437 10.3308 1.77469 10.7893 2.33976 10.7893C3.34628 10.7893 4.14971 11.1783 4.53817 11.8585C4.92663 12.5387 4.86477 13.4305 4.36157 14.2961C4.22908 14.5259 4.19383 14.7989 4.26445 15.0639C4.33507 15.3289 4.5028 15.5405 4.7324 15.6827L6.25976 16.5563C6.44523 16.6711 6.66596 16.6985 6.86895 16.645C7.08092 16.5915 7.25752 16.4504 7.37227 16.265L7.46939 16.0978C7.97259 15.2322 8.70539 14.7284 9.49119 14.7284Z"
                  fill="white"
                />
              </svg>
              <svg
                width="7"
                height="7"
                viewBox="0 0 7 7"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              >
                <path
                  d="M3.50006 7C1.56796 7 0 5.43124 0 3.49941C0 1.56759 1.56796 0 3.50006 0C5.43204 0 7 1.56759 7 3.49941C7 5.43124 5.43204 7 3.50006 7ZM3.50006 1.39928C2.34263 1.39928 1.40002 2.34296 1.40002 3.49941C1.40002 4.65706 2.34263 5.59955 3.50006 5.59955C4.65737 5.59955 5.59998 4.65706 5.59998 3.49941C5.59998 2.34296 4.65737 1.39928 3.50006 1.39928Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto myrdp-scrollbar-hide px-3 pb-4 mt-16">
        <p
          className="px-2 text-[12px] tracking-widest text-[#BABABA] animate-fadeInSoft"
          style={{ animationDelay: '120ms' }}
        >
          Main Menu
        </p>
        <div className="mt-2 space-y-1">
          {menuItems.map((item, idx) => {
            const Icon = item.icon;
            const delay = `${160 + idx * 55}ms`;
            const iconDelay = `${(idx % 8) * 120}ms`;

            if (item.action === "deposit") {
              const active = isDepositActive();
              return (
                <button
                  key={`deposit-${item.label}`}
                  type="button"
                  onClick={() => {
                    setSidebarOpen(false);
                    setShowDepositModal(true);
                  }}
                  className={`myrdp-nav-item flex w-full items-center gap-3 px-3 py-2.5 text-sm transition ${
                    active ? sidebarNavActiveClass : sidebarNavInactiveClass
                  } animate-fadeInSoft`}
                  style={{ animationDelay: delay }}
                >
                  <Icon size={16} className="myrdp-nav-icon" style={{ animationDelay: iconDelay }} />
                  <span>{item.label}</span>
                </button>
              );
            }

            return (
              <Link
                key={`${item.path}-${item.label}`}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`myrdp-nav-item flex items-center gap-3 px-3 py-2.5 text-sm transition ${
                  isActive(item.path) ? sidebarNavActiveClass : sidebarNavInactiveClass
                } animate-fadeInSoft`}
                style={{ animationDelay: delay }}
              >
                <Icon size={16} className="myrdp-nav-icon" style={{ animationDelay: iconDelay }} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>

        {user?.role === 'admin' && (
          <div className="mt-5 space-y-1 animate-fadeInSoft" style={{ animationDelay: '540ms' }}>
            <p className="px-2 text-[10px] uppercase tracking-widest text-blue-200/45">Admin</p>
            {[
              { to: '/admin', label: 'Overview', icon: FiGrid, match: (p) => p === '/admin' },
              { to: '/admin/users', label: 'Users', icon: FiUsers, match: (p) => p.startsWith('/admin/users') },
              { to: '/admin/servers', label: 'Servers', icon: FiServer, match: (p) => p.startsWith('/admin/servers') },
              {
                to: '/admin/transactions',
                label: 'Transactions',
                icon: FiActivity,
                match: (p) => p.startsWith('/admin/transactions')
              }
            ].map(({ to, label, icon: Icon, match }, i) => {
              const active = match(location.pathname);
              const delay = `${590 + i * 40}ms`;
              const iconDelay = `${(i % 8) * 120}ms`;
              return (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setSidebarOpen(false)}
                  className={`myrdp-nav-item flex items-center gap-3 px-3 py-2.5 text-sm transition ${
                    active ? sidebarNavActiveClass : sidebarNavInactiveClass
                  } animate-fadeInSoft`}
                  style={{ animationDelay: delay }}
                >
                  <Icon size={16} className="myrdp-nav-icon" style={{ animationDelay: iconDelay }} />
                  <span>{label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </nav>
      <div className="p-3 space-y-2 animate-fadeInSoft" style={{ animationDelay: '640ms' }}>
        {/* FAQs */}
        <Link
          to="/faq"
          onClick={() => setSidebarOpen(false)}
          className={`myrdp-nav-item flex w-full items-center gap-3 px-3 py-2.5 text-sm transition ${
            isActive('/faq') ? sidebarNavActiveClass : sidebarNavInactiveClass
          } animate-fadeInSoft`}
          style={{ animationDelay: '690ms' }}
        >
          <FiHelpCircle size={16} className="myrdp-nav-icon" style={{ animationDelay: '960ms' }} />
          <span>FAQs</span>
        </Link>

        {/* Support */}
        <a
          href="https://t.me/FocusedStudio"
          target="_blank"
          rel="noreferrer"
          onClick={() => setSidebarOpen(false)}
          className={`myrdp-nav-item flex w-full items-center gap-3 px-3 py-2.5 text-sm transition ${
            isActive('/support') ? sidebarNavActiveClass : sidebarNavInactiveClass
          } animate-fadeInSoft`}
          style={{ animationDelay: '740ms' }}
        >
          <svg className="myrdp-nav-icon" style={{ animationDelay: '1080ms' }} width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_33_2550)">
            <g clipPath="url(#clip1_33_2550)">
            <path d="M9.11496 11.7173L11.5798 14.5198C12.4931 15.5581 12.9497 16.0773 13.4277 15.9509C13.9057 15.8245 14.0696 15.1413 14.3974 13.7747L16.2157 6.19459C16.7206 4.08997 16.9729 3.03767 16.4118 2.51863C15.8507 1.9996 14.8782 2.38574 12.933 3.15804L3.90838 6.74105C2.35261 7.35873 1.57473 7.6676 1.52535 8.19832C1.52028 8.25262 1.5202 8.30733 1.52509 8.3616C1.57285 8.89253 2.34978 9.20405 3.90365 9.82683C4.6077 10.109 4.95974 10.2502 5.21213 10.5204C5.24051 10.5508 5.26779 10.5823 5.29394 10.6148C5.52648 10.9037 5.62574 11.283 5.8242 12.0413L6.19565 13.4608C6.38878 14.1988 6.48535 14.5679 6.73827 14.6182C6.99119 14.6685 7.2114 14.3625 7.65182 13.7505L9.11496 11.7173ZM9.11496 11.7173L8.87325 11.4655C8.59824 11.1788 8.4607 11.0355 8.4607 10.8574C8.4607 10.6793 8.59824 10.5359 8.87325 10.2493L11.5903 7.41755" stroke="white" strokeWidth="0.760455" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
            </g>
          <defs>
          <clipPath id="clip0_33_2550">
          <rect width="19" height="19" fill="white"/>
          </clipPath>
          <clipPath id="clip1_33_2550">
          <rect width="19" height="19" fill="white"/>
          </clipPath>
          </defs>
          </svg>
          <span>Support</span>
        </a>

        {/* Log Out */}
      </div>
      <div className='p-3 animate-fadeInSoft' style={{ animationDelay: '790ms' }}>
        <button
          onClick={handleLogout}
          className="myrdp-nav-item flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-blue-100/80 transition hover:bg-blue-500/10 hover:text-blue-50"
        >
          <FiLogOut size={16} className="myrdp-nav-icon" style={{ animationDelay: '1200ms' }} />
          <span>Log Out</span>
        </button>        
      </div>
    </>
  );

  return (
    <div
      className="min-h-screen overflow-x-hidden text-blue-50 bg-[#050613]"
      style={
        backgroundImage
          ? {
              backgroundImage: `url(${backgroundImage})`,
              backgroundPosition,
              backgroundSize,
              backgroundRepeat,
            }
          : undefined
      }
    >
      {/* Mobile sidebar toggle */}
      <div className="lg:hidden top-0 left-0 right-0 backdrop-blur z-40 p-4 flex items-center justify-between animate-enterUp">
        <Link to="/" className="flex items-center space-x-2">
          <img src={logo} alt="logo" className='w-10' />
          <span className="text-xl text-white">MyRDP</span>
        </Link>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-blue-100">
          {sidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 w-64 border-r border-r-[#FFFFFF1A] bg-transparent backdrop-blur-xl z-30 transform transition-transform duration-300 ease-in-out lg:translate-x-0 animate-fadeIn ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex h-full flex-col">
          {SidebarContent()}
        </div>
      </aside>

      {/* Main content: column flex so pages can stretch tables to remaining viewport height */}
      <div className="flex min-h-screen flex-col lg:ml-64 lg:min-h-screen lg:pt-0">
        <main
          className="flex min-h-0 flex-1 flex-col p-4 sm:p-6 lg:p-8 animate-enterUp"
          style={{ animationDelay: '80ms' }}
        >
          {children}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden animate-enterUp"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {showSettingsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm animate-enterUp"
          onMouseDown={() => setShowSettingsModal(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-xl rounded-2xl border border-[#B8B8B847] bg-[#212A5533] p-6 backdrop-blur-md shadow-[0_24px_50px_rgba(0,0,0,0.5)] animate-enterZoom"
            style={{ animationDelay: "80ms" }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">Account Settings</h2>
              <button
                type="button"
                onClick={() => setShowSettingsModal(false)}
                className="rounded p-1 text-white/70 hover:bg-white/10"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="flex flex-col gap-5">
              <form onSubmit={handleEmailChange} className="space-y-3 rounded-2xl border border-[#EBEBEB1C] bg-[#FFFFFF14] p-4">
                <h3 className="text-sm font-semibold text-white">Change Email</h3>
                <div className="rounded-xl border border-[#EBEBEB1C] bg-[#FFFFFF0F] px-3 py-2 text-xs text-white/75">
                  Current email: <span className="font-semibold text-white">{user?.email || '-'}</span>
                </div>
                <input
                  type="email"
                  placeholder="New email"
                  value={emailForm.newEmail}
                  onChange={(e) => setEmailForm({ ...emailForm, newEmail: e.target.value })}
                  className="w-full rounded-xl border border-[#EBEBEB1C] bg-[#FFFFFF0F] px-4 py-3 text-sm text-white outline-none placeholder:text-white/40 focus:border-blue-400/60"
                  required
                />
                <p className="text-xs text-white/60">
                  We will send a confirmation link to your current email before replacing it.
                </p>
                <button
                  type="submit"
                  disabled={savingEmail}
                  className="w-full rdp-primary-btn py-3"
                >
                  {savingEmail ? 'Sending...' : 'Send Confirmation Link'}
                </button>
              </form>

              <form onSubmit={handlePasswordChange} className="space-y-3 rounded-2xl border border-[#EBEBEB1C] bg-[#FFFFFF14] p-4">
                <h3 className="text-sm font-semibold text-white">Change Password</h3>
                <PasswordInput
                  placeholder="Current password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  autoComplete="current-password"
                  inputClassName="w-full rounded-xl border border-[#EBEBEB1C] bg-[#FFFFFF0F] px-4 py-3 pr-11 text-sm text-white outline-none placeholder:text-white/40 focus:border-blue-400/60"
                  required
                />
                <PasswordInput
                  placeholder="New password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  autoComplete="new-password"
                  inputClassName="w-full rounded-xl border border-[#EBEBEB1C] bg-[#FFFFFF0F] px-4 py-3 pr-11 text-sm text-white outline-none placeholder:text-white/40 focus:border-blue-400/60"
                  required
                />
                <PasswordInput
                  placeholder="Confirm new password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  autoComplete="new-password"
                  inputClassName="w-full rounded-xl border border-[#EBEBEB1C] bg-[#FFFFFF0F] px-4 py-3 pr-11 text-sm text-white outline-none placeholder:text-white/40 focus:border-blue-400/60"
                  required
                />
                <button
                  type="submit"
                  disabled={savingPassword}
                  className="w-full rdp-primary-btn py-3"
                >
                  {savingPassword ? 'Saving...' : 'Update Password'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {showDepositModal && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4 backdrop-blur-sm animate-enterUp"
          onMouseDown={() => setShowDepositModal(false)}
          role="presentation"
        >
          <div
            className="animate-enterZoom"
            style={{ animationDelay: "80ms" }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <DepositForm compact onClose={() => setShowDepositModal(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
